import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePatientCard } from '../../../hooks/usePatientCard';
import { useTaskFlowParam } from './useTaskFlowParam';
import { TreatmentCard } from '../../patient-files/overview/cards/TreatmentCard';
import { DmEventsCard } from '../../patient-files/overview/cards/DmEventsCard';
import { RefinementLandingPage } from '../../patient-files/actions/vca/refinement/RefinementLandingPage';
import { RetentionLandingPage } from '../../patient-files/actions/vca/retention/RetentionLandingPage';
import { DmControlRoute } from '../../patient-files/actions/dm/control/DmControlRoute';
import { useDmControlRoute } from '../../patient-files/actions/dm/control/useDmControlRoute';
import { DmNotesLanding } from '../../patient-files/actions/dm/DmNotesLanding';
import { DmPatientMessagesTab } from '../../patient-files/actions/dm/DmPatientMessagesTab';
import { PatientExternalLinks } from './PatientExternalLinks';
import { TaskSidebarPanel } from './TaskSidebarPanel';
import { TaskActionsBox } from './TaskActionsBox';
import { TaskColumnFill, TaskScrollColumn } from './TaskScrollColumn';
import { buildTxActionItems, type TxAction } from './txActionItems';
import { buildSidebarTabs, IN_TREATMENT_TABS } from './taskSidebarTabs';
import { triggerCompletion } from './triggerCompletion';
import type { TaskBodyProps } from './index';
import { OpenVirtualAppointmentsNowModal } from '../../virtual-appointments/OpenVirtualAppointmentsNowModal';
import { openNowFeedback, openedNowCompletion } from '../../virtual-appointments/openNowOutcome';
import type { OpenVirtualAppointmentsNowResult } from '../../../services/virtualAppointments';
import { useToast } from '../../../providers/ToastProvider';

type TxFlow = Exclude<TxAction, 'manage-monitoring' | 'open-vas'>;

const TX_FLOWS: readonly TxFlow[] = ['refinement', 'retention', 'dm-notes', 'messages'];

// Tx "Manage Tx", laid out like Manage VAs: treatment progress, DM events and the actions box on the
// left (30%), the patient tabs on the right (70%); an action swaps the surface to its landing flow.
export function ManageTreatmentTask({
    workCase: c,
    statusMeta,
    onPayloadChange,
    onTaskComplete
}: TaskBodyProps) {
    // In the URL, so browser Back returns from a flow to the workspace.
    const { flow: action, open: openAction, close: closeAction } = useTaskFlowParam(TX_FLOWS);
    const dmControl = useDmControlRoute(c.id);
    // Monitoring is a URL-driven modal over the workspace; the other actions swap the surface.
    // Opening VAs now is a modal over the workspace, like monitoring.
    const [openingVas, setOpeningVas] = useState(false);
    const trigger = (next: TxAction) => {
        if (next === 'manage-monitoring') dmControl.open();
        else if (next === 'open-vas') setOpeningVas(true);
        else openAction(next);
    };
    const { data } = usePatientCard(c.id);
    // Same tabs as Manage VAs; its Monitoring tab is the full monitoring view.
    const tabs = useMemo(
        () => buildSidebarTabs(c.id, IN_TREATMENT_TABS, null, { manageMonitoring: true }),
        [c.id]
    );
    const monitoring = data?.monitoring ?? null;
    const isDmLinked = monitoring?.dmId != null;
    const actionItems = buildTxActionItems(isDmLinked, trigger);

    // Read-only management surface: hide the default action footer.
    useEffect(() => {
        onPayloadChange?.({ payload: null, valid: true, bodyOwnsSubmit: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { showToast } = useToast();
    // Opening VAs (or moving on with the open ones) puts the patient in a VA step: the task is done.
    // Only planning some leaves the patient here, so the workspace stays.
    const handleVasOpened = useCallback(
        (result: OpenVirtualAppointmentsNowResult) => {
            setOpeningVas(false);
            const completed = openedNowCompletion(result);
            const feedback = openNowFeedback(result);
            if (completed) onTaskComplete?.(completed);
            else if (feedback) showToast(feedback.message, feedback.tone);
        },
        [onTaskComplete, showToast]
    );

    // A triggered refinement/retention leaves "Treatment in progress": complete with the edge it took.
    const handleTriggered = useCallback(
        (landedOn: string) =>
            onTaskComplete?.(triggerCompletion(statusMeta.outgoingActions, landedOn)),
        [onTaskComplete, statusMeta.outgoingActions]
    );

    if (action === 'refinement') {
        return (
            <div className="flex-1 min-h-0 flex flex-col">
                <RefinementLandingPage
                    patientId={c.id}
                    patientName={c.name}
                    onBack={closeAction}
                    onSubmitted={handleTriggered}
                />
            </div>
        );
    }
    if (action === 'retention') {
        return (
            <div className="flex-1 min-h-0 flex flex-col">
                <RetentionLandingPage
                    patientId={c.id}
                    patientName={c.name}
                    onBack={closeAction}
                    onSubmitted={handleTriggered}
                />
            </div>
        );
    }
    if (action === 'dm-notes') {
        return (
            <div className="flex-1 min-h-0 flex flex-col">
                <DmNotesLanding patientId={c.id} patientName={c.name} onBack={closeAction} />
            </div>
        );
    }
    if (action === 'messages') {
        return (
            <div className="flex-1 min-h-0 flex flex-col">
                <DmPatientMessagesTab patientId={c.id} onBack={closeAction} />
            </div>
        );
    }

    return (
        <div className="flex-1 min-h-0 flex gap-4 p-4 overflow-hidden">
            {/* Treatment progress and actions hug their content; DM events take the rest, never
                under 180px, and the column scrolls when that doesn't fit. */}
            <TaskScrollColumn gapClassName="gap-4">
                <TreatmentCard
                    patientId={c.id}
                    monitoring={monitoring ?? undefined}
                    hideTitle
                    fitRow
                />
                <TaskColumnFill minHeightClassName="min-h-[180px]">
                    <DmEventsCard patientId={c.id} dmId={monitoring?.dmId} fill />
                </TaskColumnFill>
                <TaskActionsBox
                    items={actionItems}
                    headerRight={<PatientExternalLinks patientId={c.id} />}
                />
            </TaskScrollColumn>

            <div className="flex-[7] min-w-0 min-h-0">
                <TaskSidebarPanel tabs={tabs} keepMounted />
            </div>
            <DmControlRoute patientId={c.id} patientName={c.name} />
            {openingVas && (
                <OpenVirtualAppointmentsNowModal
                    patientId={c.id}
                    allowMoveOnly
                    canOpenNow
                    onClose={() => setOpeningVas(false)}
                    onOpened={handleVasOpened}
                />
            )}
        </div>
    );
}
